import axios from "axios";
import { categoryMapping, industryMapping, itemsToExclude } from "./mappings";

export const fetchItems = async ({ itemId = null, setItems, setCraftables, setTypes, itemType, setUGCCreators, setObtainables } = {}) => {
  let items = [],
    marketData = [];

  try {
    const [itemsResp, marketResp] = await Promise.all([
      itemId ? axios.get(`https://api.pixelore.wiki/api/item/${itemId}`).catch((e) => e) : axios.get("https://api.pixelore.wiki/api/items").catch((e) => e),
      axios.get("https://api.pixelore.wiki/api/market-data").catch((e) => e),
    ]);

    if (itemsResp instanceof Error) {
      console.error("Item request failed:", itemsResp);
      return null;
    } else {
      items = itemId ? [itemsResp.data.item] : itemsResp.data || [];
    }

    marketData = !(marketResp instanceof Error) ? marketResp.data || [] : [];
  } catch (error) {
    console.log("Unexpected error while fetching item/market data", error);
  }

  if (marketData && items) {
    const marketPrices = marketData.data.prices;
    const marketCount = marketData.data.counts;
    const ENERGY_COST_PER_UNIT = 50;

    try {
      const itemsData = items
        .filter((item) => {
          if (itemId !== null && item.id === itemId) return true;
          if (itemType && item.type === "UGC") return true;
          if (!itemType && item.type === "Item") return true;
          return false; // Include the item
        })
        .map((item) => {
          const itemid = item.id;
          const marketPrice = marketPrices?.[itemid] ?? null;
          const itemCount = marketCount?.[itemid] ?? 0;
          const formattedMarketPrice = marketPrice ? new Intl.NumberFormat().format(marketPrice) : "N/A";
          const baseMarketPrice = marketPrice || 0;

          //Categorization Section
          const categories = item.categories ? item.categories.map((category) => categoryMapping[category] || category) : [];
          const uniqueCategoriesSet = new Set(categories);
          if (item.notes?.[0]?.type) {
            uniqueCategoriesSet.add(categoryMapping[item.notes?.[0].type] || item.notes?.[0].type);
          }

          const targetSurface = item?.placeObject?.targetSurface;
          if (Array.isArray(targetSurface)) {
            if (targetSurface.some((surface) => surface?.includes?.("wall"))) {
              uniqueCategoriesSet.add("Wall Decor");
            }
          }

          if (item.type === "UGC") {
            uniqueCategoriesSet.delete("Decoration");
          }

          const uniqueCategories = [...uniqueCategoriesSet].join(", ");
          const type = uniqueCategories;

          function calculateAllTopRecipeCosts(item) {
            // Get all top-level recipes from craftingMap
            const topLevelRecipes = item?.craftingMap?.["0"]?.allRecipes;
            if (!topLevelRecipes) return [];

            return topLevelRecipes
              .filter((recipe) => !itemsToExclude.includes(recipe.achId))
              .map((recipe) => {
                // For each recipe, calculate cost of DIRECT ingredients only (first level)
                const ingredientBreakdown = recipe.ingredients.map((ingredient) => {
                  // We only care about the direct ingredient properties
                  const ingredientId = ingredient.id;
                  const quantity = ingredient.quantity;
                  const ingredientItem = items?.find((i) => i.id === ingredientId);
                  const storePrice = ingredientItem?.stores?.[0]?.price ?? Infinity;

                  const marketPrice = marketPrices?.[ingredientId] ?? Infinity;

                  const bestPrice = Math.min(storePrice > 0 ? storePrice : Infinity, marketPrice > 0 ? marketPrice : Infinity);

                  const finalUnitPrice = bestPrice === Infinity ? 0 : bestPrice;
                  const totalCost = finalUnitPrice * quantity;

                  return {
                    id: ingredientId,
                    name: ingredient.name || "Unknown Item",
                    image: ingredient.image || null,
                    quantity: quantity,
                    unitPrice: finalUnitPrice,
                    totalCost: totalCost,
                    source: finalUnitPrice === storePrice ? "store" : "market",
                    isDirectIngredient: true, // All ingredients at this level are direct
                  };
                });

                const ingredientsTotal = ingredientBreakdown.reduce((sum, i) => sum + i.totalCost, 0);
                const energyTotal = (recipe.energy || 0) * ENERGY_COST_PER_UNIT;
                const fullCost = ingredientsTotal + energyTotal;

                return {
                  achievementId: recipe.achId,
                  name: recipe.name || "Unnamed Recipe",
                  image: recipe.image,
                  quantityProduced: recipe.actualOutputQuantity,
                  ingredients: ingredientBreakdown,
                  energy: recipe.energy,
                  energyCost: energyTotal,
                  costLessEnergy: ingredientsTotal,
                  totalCost: fullCost,
                  costPerUnit: fullCost / (recipe.quantity || 1),
                  craftingTime: recipe.minutes,
                  level: recipe.level,
                  skill: industryMapping[recipe.requiredSkill] || recipe.requiredSkill,
                  exp: recipe.exp,
                  expType: industryMapping[recipe.expType] || recipe.expType,
                  expCategory: categoryMapping[recipe.category] || recipe.category,
                  requiredMemberships: recipe.requiredMemberships || [],
                  isBestRecipe: recipe.isBestRecipe || false,
                };
              });
          }

          let recipes = !itemsToExclude.includes(item.id) ? calculateAllTopRecipeCosts(item) : [];
          const cheapestRecipe = recipes?.reduce((lowest, current) => {
            if (!lowest || current.totalCost < lowest.totalCost) {
              return current;
            }
            return lowest;
          }, null);

          const craftingCost = cheapestRecipe?.totalCost || null;
          const craftingEnergy = cheapestRecipe?.energy || null;
          const craftingCostLessEnergy = cheapestRecipe?.costLessEnergy;
          const whereToGet = cheapestRecipe?.expCategory || item?.produce?.industry || item?.produce?.expType || null;

          const waysToObtain = [];
          const shops = [];

          if (item.id === "itm_vipcoupon_notrade" || item.id === "itm_vipcoupon_1month" || item.id === "itm_vipcoupon_6month" || item.id === "itm_vipcoupon_12month") {
            shops.push("VIP Shop");
            waysToObtain.push("VIP Shop");
          }

          if (item.stores && item.stores.length > 0) {
            for (const store of item.stores) {
              shops.push(store.store);
              waysToObtain.push(store.store);
            }
          }

          if (marketPrice) {
            shops.push("Marketplace");
          }

          if (item.notes?.[0]?.obtain && Array.isArray(item.notes?.[0].obtain)) {
            waysToObtain.push(...item.notes?.[0]?.obtain);
          }

          const resultItem = item.crop?.resultId ? items.find((i) => i.id === item.crop.resultId) : null;
          const seedItem = items.find((i) => i.crop?.resultId === item.id && !itemsToExclude.includes(i.id));

          let productionCost = null;
          if (item.produce?.[0]) {
            if (item.produce[0].dropChance > 0) {
              productionCost = (100 / item.produce[0].dropChance) * item.produce[0].energy * ENERGY_COST_PER_UNIT;
            } else {
              productionCost = item.produce[0].energy * ENERGY_COST_PER_UNIT;
            }
          } else if (seedItem || item.crop) {
            if (item.crop) {
              productionCost = item.crop?.plantingEnergy * -1 + 0.5 + item.crop?.harvestEnergy * ENERGY_COST_PER_UNIT;
            } else {
              productionCost = seedItem.crop?.plantingEnergy * -1 + 0.5 + 1 * ENERGY_COST_PER_UNIT;
            }
          }

          let profit = null;
          if (productionCost && marketPrice) {
            if (seedItem) {
              profit = (seedItem.stores?.[0]?.price || seedItem.marketPrice || 0) - productionCost;
            } else {
              profit = marketPrice - productionCost;
            }
          } else if (marketPrice && craftingCost > 0) {
            profit = marketPrice - craftingCost;
          } else {
            profit = null;
          }

          const uniqueWaysToObtain = [...new Set(waysToObtain)];
          const energyValue = item.energyGiven === "N/A" || item.crop ? 0 : item.energyGiven;

          const calculateChange = (currentPrice, historicalPrice) => {
            if (!historicalPrice || historicalPrice.value === 0 || !historicalPrice.value) {
              return currentPrice > 0 ? Infinity : null;
            }
            if (currentPrice === 0) return -Infinity;
            return ((currentPrice - historicalPrice.value) / historicalPrice.value) * 100;
          };

          return {
            category: type,
            itemImage: item?.placeObject?.image ?? item?.placeEntity?.image ?? item.image,
            marketPrice: formattedMarketPrice,
            marketPieces: itemCount,
            baseMarketPrice,
            history: {
              change1h: item.marketHistory?.["1H"] ? calculateChange(baseMarketPrice, item.marketHistory["1H"]) : null,
              change1d: item.marketHistory?.["1D"] ? calculateChange(baseMarketPrice, item.marketHistory["1D"]) : null,
              change7d: item.marketHistory?.["7D"] ? calculateChange(baseMarketPrice, item.marketHistory["7D"]) : null,
              change15d: item.marketHistory?.["15D"] ? calculateChange(baseMarketPrice, item.marketHistory["15D"]) : null,
              qty1h: item.marketHistory?.["1H"]?.counts ? itemCount - item.marketHistory["1H"].counts : 0,
              qty1d: item.marketHistory?.["1D"]?.counts ? itemCount - item.marketHistory["1D"].counts : 0,
              qty7d: item.marketHistory?.["7D"]?.counts ? itemCount - item.marketHistory["7D"].counts : 0,
              qty15d: item.marketHistory?.["15D"]?.counts ? itemCount - item.marketHistory["15D"].counts : 0,
            },
            energyPerCoin: marketPrice > 0 ? energyValue / marketPrice : "N/A",
            coinPerEnergy: energyValue > 0 ? marketPrice / energyValue : "N/A",
            craftPerCoin: craftingCost > 0 ? energyValue / craftingCost : null,
            craftPerEnergy: craftingCost > 0 ? craftingCost / energyValue : null,
            whereToGet,
            craftingCost: craftingCost > 0 ? craftingCost : null,
            productionCost,
            craftingEnergy: craftingEnergy > 0 ? craftingEnergy : null,
            ingredientCost: craftingCostLessEnergy > 0 ? craftingCostLessEnergy : null,
            profit: profit,
            resultItem,
            isCrop: seedItem ? true : false,
            crop: seedItem ? seedItem.crop : item.crop,
            recipes: recipes,
            obtainable: uniqueWaysToObtain,
            purchaseable: shops,
            craftable: whereToGet ? industryMapping[whereToGet] || whereToGet : "Not Craftable",
            tradeable: item.tradable === false ? "Tradable" : "Cannot be traded",
            energyValue: energyValue,
            itemTier: item?.tier || item?.produce?.[0]?.tier || "Untiered",
            energy: item.energy || item.produce?.[0]?.energy || null,
            ...item,
          };
        });

      if (setItems) {
        setItems(itemsData);
      }

      if (setCraftables) {
        const uniqueCraftables = new Set();
        itemsData.forEach((item) => {
          if (item.craftable) {
            uniqueCraftables.add(item.craftable);
          }
        });
        setCraftables([...uniqueCraftables]);
      }

      if (setTypes) {
        const uniqueTypes = new Set();
        itemsData.forEach((item) => {
          if (item.category) {
            item.category.split(",").forEach((type) => uniqueTypes.add(type.trim()));
          }
        });

        const sortedTypes = [...uniqueTypes].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
        setTypes([...sortedTypes]);
      }

      if (setUGCCreators) {
        const uniqueCreators = new Set();
        itemsData.forEach((item) => {
          if (item.ugc?.creatorname) {
            uniqueCreators.add(item.ugc?.creatorname);
          }
        });

        const sortedCreators = [...uniqueCreators].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

        setUGCCreators(sortedCreators);
      }

      if (setObtainables) {
        const obtainables = new Set();

        itemsData.forEach((item) => {
          if (item.obtainable && Array.isArray(item.obtainable)) {
            item.obtainable.forEach((store) => {
              if (typeof store === "string" && store.trim() !== "") {
                obtainables.add(store);
              }
            });
          }
        });

        const sortedObtainables = [...obtainables]
          .filter((item) => typeof item === "string" && item.trim() !== "")
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

        setObtainables(sortedObtainables);
      }

      return itemsData;
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }
};
